<template>
  <modal :name="nomeModal" classes="v--modal cbm-modal" :adaptive="true" height="auto" :maxWidth="560" :clickToClose="!salvando" @closed="aoFecharModal">
    <div class="cbm-root">
      <h3 class="cbm-title cbm-title--perigo"><fa-icon icon="minus-circle" /> Remover saldo de cashback</h3>
      <p class="cbm-desc">Tire parte ou todo o saldo disponível do cliente. A remoção fica registrada no extrato com a justificativa.</p>

      <div class="cbm-cliente">
        <div class="cbm-cliente__icone"><fa-icon icon="wallet" /></div>
        <div class="cbm-cliente__dados">
          <div class="cbm-cliente__nome">{{ carteira.nome || `Cliente ${carteira.clienteId}` }}</div>
          <div class="cbm-cliente__codigo">Código {{ carteira.clienteId }}</div>
        </div>
        <div class="cbm-cliente__saldo">
          <div class="cbm-cliente__saldo-rotulo">Saldo disponível</div>
          <div :class="['cbm-cliente__saldo-valor', { 'cbm-cliente__saldo-valor--zerado': !temSaldo }]">{{ formatMoney(saldo) }}</div>
        </div>
      </div>

      <div class="cbm-cards" role="radiogroup" aria-label="Quanto remover">
        <label :class="['cbm-card', { 'cbm-card--ativo': modo === 'zerar', 'cbm-card--desabilitado': !temSaldo }]">
          <input v-model="modo" class="cbm-card__radio" type="radio" name="cashback-remover-modo" value="zerar" :disabled="!temSaldo" />
          <span class="cbm-card__icone cbm-card__icone--perigo"><fa-icon icon="eraser" /></span>
          <span class="cbm-card__titulo">Zerar o saldo disponível</span>
          <span class="cbm-card__texto">Remove {{ formatMoney(saldo) }} de uma vez.</span>
        </label>
        <label :class="['cbm-card', { 'cbm-card--ativo': modo === 'valor' }]">
          <input v-model="modo" class="cbm-card__radio" type="radio" name="cashback-remover-modo" value="valor" />
          <span class="cbm-card__icone cbm-card__icone--atencao"><fa-icon icon="minus-circle" /></span>
          <span class="cbm-card__titulo">Remover um valor</span>
          <span class="cbm-card__texto">Informe quanto sai do saldo.</span>
        </label>
      </div>

      <div v-if="modo === 'valor'" :class="['cbm-campo', { 'cbm-campo--invalido': valorAcimaDoSaldo }]">
        <label class="cbm-rotulo" :for="`${nomeModal}-valor`">Valor a remover *</label>
        <InputMoney :id="`${nomeModal}-valor`" :value="valor" :aria-invalid="String(valorAcimaDoSaldo)" @input="valor = $event" />
        <small v-if="valorAcimaDoSaldo" class="cbm-ajuda cbm-ajuda--erro" role="alert">O valor passa do saldo disponível ({{ formatMoney(saldo) }}).</small>
      </div>

      <div class="cbm-campo">
        <label class="cbm-rotulo" :for="`${nomeModal}-justificativa`">Justificativa *</label>
        <textarea
          :id="`${nomeModal}-justificativa`"
          v-model="justificativa"
          :class="['cbm-input', 'cbm-textarea', { 'cbm-input--invalido': justificativaCurta }]"
          rows="3"
          :maxlength="TAMANHO_MAXIMO_JUSTIFICATIVA"
          :aria-describedby="`${nomeModal}-justificativa-ajuda`"
          placeholder="Ex.: produto devolvido pelo cliente gerou cashback indevido"
        ></textarea>
        <div :id="`${nomeModal}-justificativa-ajuda`" class="cbm-ajuda">
          <span :class="{ 'cbm-ajuda--erro': justificativaCurta }">Mínimo de {{ TAMANHO_MINIMO_JUSTIFICATIVA }} caracteres. Fica visível no extrato do cliente.</span>
          <span :class="{ 'cbm-contador--ok': !justificativaCurta && justificativa.trim().length > 0 }">{{ justificativa.length }}/{{ TAMANHO_MAXIMO_JUSTIFICATIVA }}</span>
        </div>
      </div>

      <div v-if="temSaldo" class="cbm-previa" aria-live="polite">
        <span>Saldo depois</span>
        <strong>{{ formatMoney(saldoDepois) }}</strong>
      </div>

      <div class="cbm-aviso">
        <fa-icon icon="lightbulb" class="cbm-aviso__icone" />
        <span>O valor sai primeiro do saldo que vence antes. O que está reservado em orçamentos e pedidos em andamento não é removido.</span>
      </div>

      <div v-if="erro" class="cbm-erro" role="alert">
        <fa-icon icon="exclamation-triangle" class="cbm-aviso__icone" />
        <span>{{ erro }}</span>
      </div>

      <div class="cbm-footer">
        <button type="button" class="cbm-btn cbm-btn-cancel" :disabled="salvando" @click="fechar">Cancelar</button>
        <button type="button" class="cbm-btn cbm-btn-perigo" :disabled="salvando || !valido" @click="remover">
          <fa-icon :icon="salvando ? 'spinner' : modo === 'zerar' ? 'eraser' : 'minus-circle'" :spin="salvando" />
          {{ textoConfirmar }}
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import InputMoney from "@/components/InputMoney";
import { post } from "@/helpers/apiRequest";
import { formatMoney, mensagemErroApi } from "../helpers";
import modalVisivel from "./modalVisivel";

const TAMANHO_MINIMO_JUSTIFICATIVA = 5;
const TAMANHO_MAXIMO_JUSTIFICATIVA = 500;

export default {
  name: "RemoverSaldoModal",
  components: { InputMoney },
  mixins: [modalVisivel],

  props: {
    carteira: { type: Object, required: true },
    saldo: { type: Number, default: 0 },
  },

  data() {
    return {
      TAMANHO_MINIMO_JUSTIFICATIVA,
      TAMANHO_MAXIMO_JUSTIFICATIVA,
      modo: "zerar",
      valor: 0,
      justificativa: "",
      salvando: false,
      erro: null,
    };
  },

  computed: {
    temSaldo() {
      return Number(this.saldo) > 0;
    },

    valorAcimaDoSaldo() {
      return this.modo === "valor" && Number(this.valor) > Number(this.saldo);
    },

    justificativaCurta() {
      const tamanho = this.justificativa.trim().length;

      return tamanho > 0 && tamanho < TAMANHO_MINIMO_JUSTIFICATIVA;
    },

    valorRemovido() {
      return this.modo === "zerar" ? Number(this.saldo) : Number(this.valor) || 0;
    },

    saldoDepois() {
      return Math.max(0, Number(this.saldo) - this.valorRemovido);
    },

    textoConfirmar() {
      return this.modo === "zerar" ? `Zerar ${formatMoney(this.saldo)}` : `Remover ${formatMoney(this.valorRemovido)}`;
    },

    valido() {
      const justificativaOk = this.justificativa.trim().length >= TAMANHO_MINIMO_JUSTIFICATIVA;

      if (!justificativaOk || Number(this.saldo) <= 0) return false;

      return this.modo === "zerar" || (Number(this.valor) > 0 && !this.valorAcimaDoSaldo);
    },
  },

  methods: {
    formatMoney,

    aoAbrir() {
      Object.assign(this, { modo: Number(this.saldo) > 0 ? "zerar" : "valor", valor: 0, justificativa: "", erro: null });
    },

    remover() {
      if (this.salvando) return Promise.resolve();

      this.salvando = true;
      this.erro = null;

      return post(`/admin/cashback/clientes/${encodeURIComponent(this.carteira.clienteId)}/remover-saldo`, {
        zerar: this.modo === "zerar",
        valor: this.modo === "valor" ? Number(this.valor) : null,
        justificativa: this.justificativa.trim(),
      })
        .then((json) => {
          this.$emit("removido", json);
          this.$emit("update:show", false);
        })
        .catch((err) => {
          this.erro = mensagemErroApi(err);
        })
        .finally(() => {
          this.salvando = false;
        });
    },
  },
};
</script>

<style scoped src="./cashbackModal.css"></style>
<style src="./cashbackModalCaixa.css"></style>
